import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GARIS_MODES, GARIS_PAIRS, clipFor as msClip, enClipFor, garisPages, type GarisMode, type GarisPair } from "@jomcerdik/shared";
import { Art } from "../../../ui/Art";
import { t, useLang } from "../../../lib/i18n";
import { speak } from "../../../lib/speak";
import { playClip, useAudioSource } from "../../../lib/bacaAudio";
import { Screen, StudyGate, TopBar } from "../kit";
import { Burst, shuffle } from "../games/common";
import { Done, contentLang, isEn, useRecord } from "./TadikaPages";

/* Tarik Garisan: drag (or tap, then tap) from a left item to its match on the right.
   Warna: colour to name. Besar dan Kecil: small letter to capital. Binatang: join the two halves. Pekerjaan: picture to name.
   Bahasa Melayu for BM, English for every other app language. Names without a recorded clip use the device voice. */

const RIGHT = () => (isEn() ? "Right! Well done!" : "Betul! Pandai!");
const TRY = () => (isEn() ? "Try again." : "Cuba lagi.");
const BETUL = () => (isEn() ? enClipFor("line", RIGHT()) : msClip("line", RIGHT()));
const CUBA = () => (isEn() ? enClipFor("line", TRY()) : msClip("line", TRY()));

/** Say a colour, animal or job name. */
function sayName(text: string) {
  if (isEn()) { playClip(enClipFor("word", text), text, 0.7); return; }
  try { playClip(msClip("word", text), text, 0.7); } catch { speak(text, 0.7); }
}
/** Say a letter: "Letter A." in English, the device voice in BM. */
function sayLetter(l: string) {
  if (isEn()) playClip(enClipFor("line", `Letter ${l.toUpperCase()}.`), l, 0.7);
  else speak(l, 0.7);
}

export const GARIS_META: Record<GarisMode, { title: string; sub: string; art: string; tone: string }> = {
  warna: { title: "Padan Warna", sub: "Tarik garisan: padankan warna dengan namanya.", art: "mascot/girl-paint", tone: "from-pink-400 to-orange-400" },
  huruf: { title: "Besar dan Kecil", sub: "Tarik garisan: padankan huruf kecil dengan huruf besar.", art: "huruf/huruf-a", tone: "from-amber-300 to-orange-500" },
  haiwan: { title: "Padan Binatang", sub: "Tarik garisan: sambung dua bahagian binatang.", art: "haiwan/gajah", tone: "from-lime-400 to-green-600" },
  kenderaan: { title: "Padan Kenderaan", sub: "Tarik garisan: padankan gambar dengan nama kenderaan.", art: "kenderaan/kereta", tone: "from-cyan-400 to-sky-600" },
  buah: { title: "Padan Buah", sub: "Tarik garisan: padankan gambar dengan nama buah.", art: "buah/epal", tone: "from-yellow-400 to-red-500" },
  pekerjaan: { title: "Padan Pekerjaan", sub: "Tarik garisan: padankan gambar dengan nama pekerjaan.", art: "pekerjaan/doktor", tone: "from-indigo-400 to-blue-600" },
};

export function TadikaGarisPage() {
  return <Screen><StudyGate><GarisGame /></StudyGate></Screen>;
}

function GarisGame() {
  const raw = useParams().mode as GarisMode;
  const mode: GarisMode = GARIS_MODES.includes(raw) ? raw : "warna";
  const meta = GARIS_META[mode];
  const appLang = useLang();
  const [seed, setSeed] = useState(0);
  return (
    <>
      <TopBar back="/belajar/tadika" title={t(meta.title)} subtitle={t(meta.sub)} />
      <Rounds key={`${mode}${seed}${appLang}`} mode={mode} onAgain={() => setSeed((s) => s + 1)} />
    </>
  );
}

function Rounds({ mode, onAgain }: { mode: GarisMode; onAgain: () => void }) {
  const pages = useMemo(() => garisPages(mode, contentLang()), [mode]);
  const [page, setPage] = useState(0);
  const [score, setScore] = useState(0);
  const record = useRecord(0, `garis-${mode}`);
  const nav = useNavigate();
  useAudioSource(useMemo(() => [BETUL(), CUBA()], []));
  const max = pages.length * GARIS_PAIRS;
  if (page >= pages.length) return <Done score={score} max={max} onAgain={onAgain} onDone={() => nav("/belajar/tadika")} save={record} />;
  return (
    <Board
      key={page}
      mode={mode}
      pairs={pages[page]!}
      page={page}
      pageCount={pages.length}
      onNext={(firstTry) => { setScore((s) => s + firstTry); setPage((p) => p + 1); }}
    />
  );
}

type Point = { x: number; y: number };

function Board({ mode, pairs, page, pageCount, onNext }: { mode: GarisMode; pairs: GarisPair[]; page: number; pageCount: number; onNext: (firstTry: number) => void }) {
  const rightOrder = useMemo(() => shuffle(pairs), [pairs]);
  const box = useRef<HTMLDivElement>(null);
  const nodes = useRef(new Map<string, HTMLElement>());
  const [, force] = useState(0);
  const [links, setLinks] = useState<string[]>([]); // keys joined correctly
  const [missed, setMissed] = useState<string[]>([]); // keys that had a wrong try
  const [bad, setBad] = useState<{ l: string; r: string } | null>(null);
  const [drag, setDrag] = useState<{ l: string; to: Point; moved: boolean } | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [burst, setBurst] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  // Lines are drawn from measured positions, so measure again after layout and on resize (images load later).
  useLayoutEffect(() => {
    force((n) => n + 1);
    const el = box.current; if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => force((n) => n + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const anchor = (id: string, side: "L" | "R"): Point | null => {
    const el = nodes.current.get(`${side}${id}`), b = box.current?.getBoundingClientRect();
    if (!el || !b) return null;
    const r = el.getBoundingClientRect();
    return { x: (side === "L" ? r.right : r.left) - b.left, y: r.top + r.height / 2 - b.top };
  };
  const local = (e: { clientX: number; clientY: number }): Point => {
    const b = box.current!.getBoundingClientRect();
    return { x: e.clientX - b.left, y: e.clientY - b.top };
  };
  const pairOf = (key: string) => pairs.find((p) => p.key === key)!;

  function hear(side: "L" | "R", p: GarisPair) {
    if (mode === "huruf") sayLetter(side === "L" ? p.left : p.right);
    else if (side === "R" || mode === "warna" || mode === "pekerjaan") sayName(p.right);
  }

  function connect(l: string, r: string) {
    setSel(null);
    if (links.includes(l)) return;
    if (l === r) {
      setLinks((x) => [...x, l]); setBurst((n) => n + 1);
      const p = pairOf(l);
      if (mode === "huruf") sayLetter(p.right); else sayName(p.right);
      timer.current = setTimeout(() => playClip(BETUL(), RIGHT()), 900);
    } else {
      setMissed((m) => (m.includes(l) ? m : [...m, l]));
      setBad({ l, r }); playClip(CUBA(), TRY());
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setBad(null), 800);
    }
  }

  function down(e: PointerEvent<HTMLElement>, key: string) {
    if (links.includes(key)) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ l: key, to: local(e), moved: false });
  }
  function move(e: PointerEvent<HTMLElement>) {
    setDrag((d) => (d ? { ...d, to: local(e), moved: true } : d));
  }
  function up(e: PointerEvent<HTMLElement>, key: string) {
    const d = drag; setDrag(null);
    if (!d) return;
    const target = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>("[data-r]");
    if (target?.dataset.r) { connect(key, target.dataset.r); return; }
    // A tap (no drag): hear it and wait for a tap on the right-hand side.
    const start = anchor(key, "L"), end = local(e);
    const far = start ? Math.hypot(end.x - start.x, end.y - start.y) > 24 : false;
    if (!far) { setSel(sel === key ? null : key); hear("L", pairOf(key)); }
  }

  const finished = links.length === pairs.length;
  const line = (a: Point, b: Point, cls: string, key: string) => (
    <path key={key} d={`M${a.x},${a.y} C${a.x + 40},${a.y} ${b.x - 40},${b.y} ${b.x},${b.y}`} className={cls} fill="none" strokeWidth={5} strokeLinecap="round" />
  );

  return (
    <div className="space-y-3 rounded-3xl bg-card p-4 shadow-sm ring-1 ring-black/5">
      <Burst show={burst} />
      <div className="flex items-center justify-between text-xs font-bold text-slate-400">
        <span>{page + 1}/{pageCount}</span>
        <span>{links.length}/{pairs.length}</span>
      </div>
      <div ref={box} className="relative mx-auto flex max-w-md select-none justify-between gap-10 py-1" style={{ touchAction: "none" }}>
        <svg className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" aria-hidden>
          {links.map((k) => { const a = anchor(k, "L"), b = anchor(k, "R"); return a && b ? line(a, b, "stroke-emerald-500", `ok${k}`) : null; })}
          {bad && (() => { const a = anchor(bad.l, "L"), b = anchor(bad.r, "R"); return a && b ? line(a, b, "stroke-rose-400", "bad") : null; })()}
          {drag && drag.moved && (() => { const a = anchor(drag.l, "L"); return a ? line(a, drag.to, "stroke-sky-500", "drag") : null; })()}
        </svg>
        <div className="flex flex-col justify-around gap-3">
          {pairs.map((p) => (
            <div
              key={p.key}
              ref={(el) => { if (el) nodes.current.set(`L${p.key}`, el); }}
              onPointerDown={(e) => down(e, p.key)}
              onPointerMove={move}
              onPointerUp={(e) => up(e, p.key)}
              onPointerCancel={() => setDrag(null)}
              role="button"
              tabIndex={0}
              aria-label={mode === "huruf" ? p.left : p.right}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel(sel === p.key ? null : p.key); hear("L", p); } }}
              className={`grid cursor-grab place-items-center rounded-2xl bg-white p-2 ring-2 transition ${links.includes(p.key) ? "ring-emerald-400" : sel === p.key || drag?.l === p.key ? "ring-sky-400" : "ring-slate-200"}`}
            >
              <LeftFace mode={mode} p={p} />
            </div>
          ))}
        </div>
        <div className="flex flex-col justify-around gap-3">
          {rightOrder.map((p) => (
            <button
              key={p.key}
              type="button"
              data-r={p.key}
              ref={(el) => { if (el) nodes.current.set(`R${p.key}`, el); }}
              onClick={() => { if (sel) connect(sel, p.key); else hear("R", p); }}
              className={`min-h-16 min-w-20 rounded-2xl bg-white px-4 text-xl font-extrabold text-slate-800 ring-2 transition ${links.includes(p.key) ? "ring-emerald-400" : bad?.r === p.key ? "ring-rose-400" : "ring-slate-200"}`}
            >
              {mode === "haiwan" ? (
                <span className="flex flex-col items-center py-1">
                  <span className="relative block h-16 w-8 overflow-hidden"><Art id={p.art!} alt="" className="absolute -left-8 top-0 h-16 w-16 max-w-none" /></span>
                  <span className={`text-sm font-extrabold ${links.includes(p.key) ? "text-emerald-600" : "sr-only"}`}>{p.right}</span>
                </span>
              ) : p.right}
            </button>
          ))}
        </div>
      </div>
      {finished ? (
        <button type="button" onClick={() => onNext(pairs.length - missed.length)} className="w-full animate-rise rounded-full bg-violet-500 py-3 text-lg font-extrabold text-white shadow-md">
          {page + 1 < pageCount ? t("Seterusnya") : t("Selesai")}
        </button>
      ) : (
        <p className="text-center text-xs text-slate-400">{t("Tarik dari kiri ke kanan, atau ketik kiri kemudian kanan.")}</p>
      )}
    </div>
  );
}

function LeftFace({ mode, p }: { mode: GarisMode; p: GarisPair }): ReactNode {
  if (mode === "warna") {
    return (
      <svg viewBox="0 0 64 64" className="h-16 w-16" aria-hidden>
        <path d="M10 22 h44 l-5 36 a4 4 0 0 1 -4 3 h-26 a4 4 0 0 1 -4 -3z" fill={p.left} stroke="#475569" strokeWidth="3" strokeLinejoin="round" />
        <ellipse cx="32" cy="22" rx="22" ry="7" fill={p.left} stroke="#475569" strokeWidth="3" />
        <path d="M16 12 a16 14 0 0 1 32 0" fill="none" stroke="#475569" strokeWidth="3" strokeLinecap="round" />
      </svg>
    );
  }
  if (mode === "huruf") return <span className="grid h-16 w-16 place-items-center text-4xl font-black text-sky-600">{p.left}</span>;
  if (mode === "haiwan") {
    // The left half of the animal; the right half is on the other side.
    return (
      <span className="flex flex-col items-center">
        <span className="relative block h-16 w-8 overflow-hidden"><Art id={p.art!} alt="" className="absolute left-0 top-0 h-16 w-16 max-w-none" /></span>
      </span>
    );
  }
  return <Art id={p.art!} alt={p.right} className="h-16 w-16" />;
}
